import {AuthContext, INotificationSeverity, NotificationContext, useApi} from "@buildwitt/shapeshift-identity";
import {createContext, ReactElement, FC, useState, useEffect, useContext} from "react";
import {Socket, io} from "socket.io-client";
import {PageBaseContext} from "./pageBaseContext";
import {useNotifications} from "@buildwitt/shapeshift-common/v1/hooks";
import {Button, LinearProgress} from "@mui/material";
import {RestrictedPage} from "../components/pageBase/restrictedPage";


const SERVER_URL = process.env.REACT_APP_API_HOST;
if (!SERVER_URL) throw new Error("REACT_APP_API_HOST required");

type SocketContextState = Socket | undefined;
const initialState: SocketContextState = undefined;
const SocketContext = createContext<SocketContextState>(initialState);

interface IProps {
	children: ReactElement;
}

const SocketProvider: FC<IProps> = ({children}) => {
	const {notify} = useContext(NotificationContext);
	const [socket, setSocket] = useState<Socket | undefined>(undefined);
	const {user, authenticated} = useContext(AuthContext);
	const timezone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)
	const [{data: socketJwt, isLoading, statusCode}] = useApi<boolean>({url: "/api/access", data: {timezone}});
	const [, setPageBaseContext] = useContext(PageBaseContext);
	const {notifications, getNotifications} = useNotifications(1, 0);


	useEffect(() => {
		if (!socketJwt || !authenticated || !user) return;

		const username = user?.email;
		if (!username) return;

		const socket = io(SERVER_URL, {
			auth: {
				strategy: "jwt",
				token: socketJwt
			},
			transports: ["websocket", "polling"]
		});

		const handleNotification = (e: { data: { body: string; subject: string } }): void => {
			const parser = new DOMParser();
			const doc = parser.parseFromString(e.data.body, "text/html");
			const body = doc.body.textContent || "";

			notify(body, e.data.subject, INotificationSeverity.neutral, 5000, false);
			getNotifications(1, 0, { cache: false });
		};

		socket.on("connect", () => {
			socket.on("notification", handleNotification);
		});

		setSocket(socket);

		return () => {
			socket.disconnect();
			socket.close();
		};
	}, [user, socketJwt, authenticated]);

	useEffect(() => {
		if (notifications?.totalCount === undefined)
			return;	
		const notificationCount = notifications?.totalCount;
		setPageBaseContext(prev => ({...prev, notificationCount}))
	}, [notifications])

	const redirectToAccountPage = (): void => {
		window.location.href = (process.env.REACT_APP_PROPELAUTH_ACCOUNT_SETTINGS_URI ?? "")
	}


	if (isLoading || !statusCode)
		return <LinearProgress />

	if (statusCode === 403) {
		const backBtn = <Button onClick={redirectToAccountPage}>BACK TO PROFILE</Button>;
		return <RestrictedPage
			title="Permission Required"
			subtitle="This page requires you to be logged in to access it."
			buttons={[backBtn]}
			header={<img src="/forbidden_backdrop.png" />}
		>
		</RestrictedPage>;
	}


	return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};

export {SocketProvider, SocketContext};
